import { LitElement, html, nothing } from 'lit';
import MicroModal from 'micromodal';
import { styles } from './css';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import {
  adobeCleanString,
  analyticsCtaDataObject,
  analyticsLabelFromString,
  fireAdobeAnalyticsEvent,
  handleAdobeNavEvent,
} from '../../../../helpers/adobe-analytics';
import { useAddToCartModal } from '../../../../helpers/helpers';

import {
  SHARED_CLASSES,
  SHARED_ELEMENTS,
} from '@Root/lib/lit-3form-block/styles.js';

import { LIT_CSS_VARIABLES, GRID_LAYOUT } from '@Root/lib/lit-styles.js';
export class WalterMaterialsBlock extends LitElement {
  static get properties() {
    return {
      content: { type: Object },
      sampleCta: { type: Object },
    };
  }

  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      GRID_LAYOUT,
      styles,
    ];
  }

  constructor() {
    super();

    this.content = JSON.parse(this.getAttribute('content'));
    this.sampleCta = {};
  }

  async connectedCallback() {
    super.connectedCallback();
  }

  render() {
    return html`
      ${this.materialTabsHTML}
      <div id="materials-block">
        ${this.materialData
          ? html`
              <div class="header column">
                <h2>${this.formattedHeaderStr}</h2>
                <h3>
                  Specifications & Documentation
                </h3>
                <img class="sample-img" src=${this.materialData.imageUrl} />
                ${this.sampleCta.unavailable
                  ? html`
                      <cta-button
                        cta-data=${this.sampleCta.fallbackCtaData}
                        cta-type="primary"
                        @click="${this.fallbackCtaClicked}"
                      ></cta-button>
                    `
                  : html`
                      <cta-button
                        cta-data=${this.orderSampleCtaData}
                        cta-type="primary"
                        @click="${this.orderSampleClicked}"
                        .disabled="${this.sampleCta.disabled}"
                      ></cta-button>
                    `}
                ${this.materialData.fullPanelOrdering
                  ? html`
                      <cta-button
                        cta-data=${this.panelOrderCtaData}
                        cta-type="secondary"
                        @click="${this.panelOrderClicked}"
                      ></cta-button>
                    `
                  : html``}
              </div>
            `
          : html``}
        ${this.materialDetailsData
          ? html`
              <div class="material column">
                <h4>${this.materialDetailsData.title}</h4>
                <div class="column-content">
                  ${this.materialDetailsData.rows.map(
                    row => html`
                      <div class="row">
                        <p class="row-header">${unsafeHTML(row.header)}</p>
                        <p class="row-body">
                          ${this.renderRowContent(row.text || row.content)}
                        </p>
                      </div>
                    `
                  )}
                </div>
              </div>
            `
          : html``}
        ${this.sustainabilityData
          ? html`
              <div class="sustainability column">
                <h4>${this.sustainabilityData.title}</h4>
                <div class="column-content">
                  <div class="row">
                    <p class="row-body">${this.sustainabilityData.text}</p>
                  </div>
                  <div class="row">
                    <p class="row-header">Sustainability Documentation</p>
                    <div class="sustainability-downloads">
                      ${this.sustainabilityDownloads.length > 0
                        ? this.sustainabilityDownloads.map(sustainDownload => {
                            return html`
                              <div class="downloads-wrapper print-none">
                                <cta-text
                                  .ctaData=${sustainDownload}
                                  .ctaAnalyticsData=${analyticsCtaDataObject(
                                    sustainDownload,
                                    'Material Details'
                                  )}
                                  hide-right-arrow-svg="true"
                                  target-blank="true"
                                  left-download-svg="true"
                                ></cta-text>
                              </div>
                            `;
                          })
                        : html``}
                    </div>
                  </div>
                </div>
              </div>
            `
          : html``}
        ${this.downloadsData
          ? html`
              <div class="download column">
                <h4>${this.downloadsData.title}</h4>
                <div class="column-content">
                  ${this.downloadsList.length > 0
                    ? this.downloadsList.map(download => {
                        return html`
                          <cta-button
                            cta-data=${this.downloadCtaData(download)}
                            cta-type="secondary"
                          ></cta-button>
                        `;
                      })
                    : html``}
                </div>
              </div>
            `
          : html``}
      </div>
    `;
  }

  // Each tab is the same colorway in another material, a full page load away.
  get materialTabs() {
    return this.content?.materialTabs || [];
  }

  get materialTabsHTML() {
    if (this.materialTabs.length < 2) return html``;

    return html`
      <nav class="material-tabs print-none" aria-label="Material">
        ${this.materialTabs.map(
          tab => html`
            <a
              class=${tab.selected ? 'material-tab selected' : 'material-tab'}
              href=${tab.url}
              aria-current=${tab.selected ? 'page' : nothing}
              @click="${event => this.materialTabClicked(tab, event)}"
            >
              ${tab.label}
            </a>
          `
        )}
      </nav>
    `;
  }

  materialTabClicked(tab, event) {
    if (tab.selected) {
      event.preventDefault();
      return;
    }

    handleAdobeNavEvent({
      navString: `NAV|Material Details Tab|${adobeCleanString(tab.label)}`,
      destinationUrl: tab.url,
      event,
    });
  }

  fallbackCtaClicked() {
    // cta-button reports a CTA carrying its own analyticsName (visualizer).
    const cta = JSON.parse(this.sampleCta.fallbackCtaData || '{}');
    if (cta.analyticsName) return;

    // Walter's handler, as for its own fallback: quote prefill and NAV event.
    const useWalterFallbackCta = new CustomEvent('useWalterFallbackCta', {
      detail: { cta: cta.text },
      bubbles: true,
      composed: true,
    });

    this.dispatchEvent(useWalterFallbackCta);
  }

  get barrelsData() {
    return this.content.barrelsData || [];
  }

  get materialDetailsData() {
    return this.barrelsData.find(barrel => barrel.title === 'Material Details');
  }

  get sustainabilityData() {
    return this.barrelsData.find(barrel => barrel.title === 'Sustainability');
  }

  get sustainabilityDownloads() {
    return this.sustainabilityData?.downloads || [];
  }

  get downloadsData() {
    return this.barrelsData.find(barrel => barrel.title === 'Downloads');
  }

  get downloadsList() {
    return this.downloadsData.downloads;
  }

  get formattedHeaderStr() {
    let materialStr = this.materialData?.material || '';
    let headerStr = this.materialData?.headerString || '';
    if (headerStr.toLowerCase().startsWith(materialStr.toLowerCase())) {
      headerStr = headerStr.slice(materialStr.length);
    }
    return `${materialStr} | ${headerStr}`;
  }

  downloadCtaData(download) {
    download.fullWidth = true;
    download.target = '_blank';
    download.isDownload = true;
    download.placement = 'Material Details';
    return JSON.stringify(download);
  }

  get materialData() {
    return this.content.materialData;
  }

  get orderSampleCtaData() {
    return JSON.stringify({
      text: 'Order a Sample',
      url: '',
      fullWidth: true,
    });
  }

  orderSampleClicked() {
    // A disabled cta-button still lets the click reach this listener.
    if (this.sampleCta.disabled) return;

    if (this.content.sampleData) {
      this.openSampleOrderModal();
    } else {
      const useWalterOrderSample = new CustomEvent('useWalterOrderSample', {
        bubbles: true,
        composed: true,
      });

      this.dispatchEvent(useWalterOrderSample);
    }
  }

  openSampleOrderModal() {
    useAddToCartModal();
    const setupAddToCartDataEvent = new CustomEvent('setupAddToCartData', {
      detail: this.content.sampleData,
      bubbles: true,
      composed: true,
    });
    const el = document.getElementById('modal-request-sample');
    el.dispatchEvent(setupAddToCartDataEvent);
    setTimeout(() => {
      this.showMicroModal();
    }, 10);

    // Same string Walter sends when it opens the modal on a material page.
    const { headerStr } = this.content.sampleData;
    fireAdobeAnalyticsEvent({
      type: 'click',
      detail: { eVar8: `INP|Sample Order Modal Open|${headerStr}` },
    });
  }

  showMicroModal() {
    MicroModal.show('modal-request-sample');
  }

  // Matches Walter's own Order Panel CTA; cta-button sends analyticsName.
  get panelOrderCtaData() {
    return JSON.stringify({
      text: 'Order 48" x 96" Panel',
      url: '',
      fullWidth: true,
      analyticsName: `INP|Open Order Panel|${this.materialData.headerString}`,
    });
  }

  panelOrderClicked() {
    const expandedOrderSheetBarrel = new CustomEvent(
      'expandedOrderSheetBarrel',
      {
        bubbles: true,
        composed: true,
      }
    );

    this.dispatchEvent(expandedOrderSheetBarrel);
  }

  renderRowContent(rowContent) {
    if (typeof rowContent !== 'object') return this.rowContentText(rowContent);

    if (Array.isArray(rowContent))
      return html`
        ${rowContent.map(this.rowContentLink.bind(this))}
      `;

    return html`
      ${this.rowContentLink(rowContent)}
    `;
  }

  rowContentLink(link) {
    return !!link && typeof link === 'object'
      ? html`
          <a
            href=${link.url || ''}
            @click="${event => this.handleRowLinkClick(link, event)}"
          >
            ${this.rowContentText(link.text || '')}
          </a>
        `
      : html``;
  }

  handleRowLinkClick(link, event) {
    if (!link.url) return;
    const placement = this.content?.materialData?.name || 'Material Details';
    handleAdobeNavEvent({
      navString: `NAV|${analyticsLabelFromString(
        placement
      )}|${analyticsLabelFromString(link.text || link.url)}`,
      destinationUrl: link.url,
      event,
    });
  }

  rowContentText(text) {
    text = text || '';
    // remove any empty <p> tags coming from the CMS editor
    text = text.replaceAll('<p><br /></p>', '');
    return html`
      <p class="row-content-text">
        ${unsafeHTML(text)}
      </p>
    `;
  }
}
customElements.define('material-details-block', WalterMaterialsBlock);
